/**
 * The filter row above the ledger.
 *
 * Its own file rather than 90 more lines in `index.tsx`: the panel below it is
 * a fetch plus a table, and six controls with their option lists is a separate
 * job with a separate reason to change (AGENTS.md §4).
 *
 * The action dropdown is filtered by the chosen module rather than being a flat
 * list of everything. With thirty actions catalogued the flat list is already
 * longer than the screen, and "module, then one of its actions" is how somebody
 * actually narrows a log.
 */
import { X } from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../../i18n/I18nProvider";
import { auditActionLabel, auditModule } from "../../../../lib/auditAction";
import type { CrmLogFacets } from "../../../../lib/crmLogs";
import { Btn, MenuField, SearchField, Toolbar, ToolbarGap } from "../../../ui";
import { ANY, boundDate, isFiltered, moduleLabel, type LogFilters } from "./lib";

/**
 * The two date pills, in the toolbar's dress rather than the dialog's.
 *
 * `index.css` gives every `DatePicker` the sunken 52px form field, which is
 * wrong in a row of raised 44px filter pills; `crm-fld-date` is the raised one
 * the register's date filter already wears. `crm-fld` comes along only for the
 * phone rule that lets a toolbar pill share a line instead of holding 176px.
 */
const DATE_PILL = "crm-fld crm-fld-date w-44";

export function LogsToolbar({
  filters,
  onChange,
  onClear,
  facets,
  staff,
}: {
  filters: LogFilters;
  onChange: (next: LogFilters) => void;
  onClear: () => void;
  facets: CrmLogFacets | null;
  staff: { id: string; name: string }[];
}) {
  const t = useT();
  const { locale } = useI18n();
  const set = (patch: Partial<LogFilters>) => onChange({ ...filters, ...patch });

  const modules = facets?.modules ?? [];
  // Only the actions belonging to the chosen module, and all of them when no
  // module is chosen. Choosing a module also clears a stale action below, or
  // the pair would ask for an impossible intersection and return nothing.
  const actions = (facets?.actions ?? []).filter(
    (a) => filters.module === ANY || auditModule(a.key) === filters.module,
  );

  return (
    <Toolbar>
      <SearchField
        value={filters.q}
        onChange={(q) => set({ q })}
        placeholder={t("settings.logs.searchPlaceholder")}
      />

      <MenuField
        value={filters.module}
        onChange={(module) => set({ module, action: ANY })}
        label={t("settings.logs.filterModule")}
        options={[
          { value: ANY, label: t("settings.logs.anyModule") },
          ...modules.map((m) => ({ value: m.key, label: `${moduleLabel(m.key, t)} (${m.count})` })),
        ]}
      />

      <MenuField
        value={filters.action}
        onChange={(action) => set({ action })}
        label={t("settings.logs.filterAction")}
        options={[
          { value: ANY, label: t("settings.logs.anyAction") },
          ...actions.map((a) => ({ value: a.key, label: `${auditActionLabel(a.key, t)} (${a.count})` })),
        ]}
      />

      <MenuField
        value={filters.actorUserId}
        onChange={(actorUserId) => set({ actorUserId })}
        label={t("settings.logs.filterActor")}
        options={[
          { value: ANY, label: t("settings.logs.anyActor") },
          ...staff.map((s) => ({ value: s.id, label: s.name })),
        ]}
      />

      {/* The shared calendar rather than `<input type="date">`. The native one
          is a different control in every browser - a bare text box in some, a
          system field in others - and it was the only pair on this row not
          drawn by the kit. Each end bounds the other, so the calendar cannot
          offer a range that reads backwards. */}
      <DatePicker
        value={filters.from}
        onChange={(from) => set({ from })}
        toDate={boundDate(filters.to)}
        locale={locale}
        placeholder={t("settings.logs.filterFrom")}
        ariaLabel={t("settings.logs.filterFrom")}
        className={DATE_PILL}
      />
      <DatePicker
        value={filters.to}
        onChange={(to) => set({ to })}
        fromDate={boundDate(filters.from)}
        locale={locale}
        placeholder={t("settings.logs.filterTo")}
        ariaLabel={t("settings.logs.filterTo")}
        className={DATE_PILL}
      />

      <ToolbarGap />

      {isFiltered(filters) ? (
        <Btn small onClick={onClear}>
          <X className="size-4" />
          {t("settings.logs.clear")}
        </Btn>
      ) : null}
    </Toolbar>
  );
}
